import {
  FileInfo,
  OptionInfo,
  OptionVersionSimple,
  Project,
  ProjectSection,
  ProjectSubsection,
  ShareLink,
  ShareLinkAsset,
  ShareLinkAssetEntityTypes,
  TopicInfo,
} from '../../shared/interfaces';
import { Release, ReleaseAssetItem, ReleaseAssetTypes } from './release';

export enum TreeNodeType {
  SubsectionRoot = 'subsection',
  Topic = 'topic',
  Option = 'option',
  Version = 'version',
  KpfRoot = 'kpf',
  KpfFolder = 'kpf-folder',
  File = 'file',
  KpfFile = 'kpf-file',
  ShareLinkRoot = 'share-link-root',
  ShareLinkItem = 'share-link-item',
  ShareLinkAsset = 'share-link-asset',
  ReleaseRoot = 'release-root',
  ReleaseItem = 'release-item',
  ReleaseAsset = 'release-asset',
  ProjectRoot = 'project-root',
  ProjectSection = 'project-section',
  CustomNode = 'custom-node',
}

export interface BaseTreeNode {
  id: string;
  name: string;
  type: TreeNodeType;
  lastUpdated?: number;
  children: BaseTreeNode[];
  parent: BaseTreeNode | null;
  isExpanded?: boolean;
  isSelected?: boolean;
  isExpandable: boolean;
  depth: number;
}

//#region subsection node types

export interface ProjectRootNode extends BaseTreeNode {
  type: TreeNodeType.ProjectRoot;
  data: Project;
}

export interface ProjectSectionNode extends BaseTreeNode {
  type: TreeNodeType.ProjectSection;
  data: ProjectSection;
}

export interface SubsectionRootNode extends BaseTreeNode {
  type: TreeNodeType.SubsectionRoot;
  data: ProjectSubsection;
  projectId: string;
}

export interface SectionNode extends BaseTreeNode {
  type: TreeNodeType.ProjectSection;
  data: ProjectSection;
}

export interface SubsectionTopicNode extends BaseTreeNode {
  type: TreeNodeType.Topic;
  data: TopicInfo;
}

export interface SubsectionOptionNode extends BaseTreeNode {
  type: TreeNodeType.Option;
  data: OptionInfo;
}

export interface SubsectionVersionNode extends BaseTreeNode {
  type: TreeNodeType.Version;
  data: OptionVersionSimple;
}

export interface SubsectionFileNode extends BaseTreeNode {
  type: TreeNodeType.File;
  data: FileInfo;
  fileType?: string;
}

export type SubsectionNode =
  | ProjectRootNode
  | ProjectSectionNode
  | SubsectionRootNode
  | SubsectionTopicNode
  | SubsectionOptionNode
  | SubsectionVersionNode
  | SubsectionFileNode;

export const isSubsectionRootNode = (node: BaseTreeNode): node is SubsectionRootNode => {
  return node.type === TreeNodeType.SubsectionRoot;
};

export const isSectionNode = (node: BaseTreeNode): node is SectionNode => {
  return node.type === TreeNodeType.ProjectSection;
};

export const isSubsectionTopicNode = (node: BaseTreeNode): node is SubsectionTopicNode => {
  return node.type === TreeNodeType.Topic;
};

export const isSubsectionOptionNode = (node: BaseTreeNode): node is SubsectionOptionNode => {
  return node.type === TreeNodeType.Option;
};

export const isSubsectionVersionNode = (node: BaseTreeNode): node is SubsectionVersionNode => {
  return node.type === TreeNodeType.Version;
};

export const isSubsectionFileNode = (node: BaseTreeNode): node is SubsectionFileNode => {
  return node.type === TreeNodeType.File;
};

//#endregion

//#region KPF node types

export interface KpfFolderNode extends BaseTreeNode {
  type: TreeNodeType.KpfRoot | TreeNodeType.KpfFolder;
  size?: number;
  filesStatus?: 'loading' | 'loaded' | 'pending';
  subFolderIds?: string[];
  fileIds?: string[];
}

export interface KpfFileNode extends BaseTreeNode {
  type: TreeNodeType.KpfFile;
  data: FileInfo;
}

export interface KpfRootNode extends BaseTreeNode {
  type: TreeNodeType.KpfRoot;
}

export const isKpfRootNode = (node: BaseTreeNode): node is KpfFolderNode => {
  return node.type === TreeNodeType.KpfRoot;
};

export type KpfNode = KpfFolderNode | KpfFileNode;

export const isKpfFolderNode = (node: BaseTreeNode): node is KpfNode => {
  return node.type === TreeNodeType.KpfRoot || node.type === TreeNodeType.KpfFolder;
};

export const isKpfFileNode = (node: BaseTreeNode): node is KpfFileNode => {
  return node.type == TreeNodeType.KpfFile;
};

//#endregion

//#region Share Link node types

export type ShareLinkNode = ShareLinkItemNode | ShareLinkRootNode;

export const isShareLinkNode = (node: BaseTreeNode): node is ShareLinkNode => {
  return node.type == TreeNodeType.ShareLinkRoot || node.type == TreeNodeType.ShareLinkItem;
};

export interface ShareLinkItemNode extends BaseTreeNode {
  type: TreeNodeType.ShareLinkItem;
  data: ShareLink;
}

export interface ShareLinkRootNode extends BaseTreeNode {
  type: TreeNodeType.ShareLinkRoot;
}

export interface ShareLinkAssetNode extends BaseTreeNode {
  type: TreeNodeType.ShareLinkAsset;
  assetType: ShareLinkAssetEntityTypes;
  data: ShareLinkAsset;
}

export type ShareLinkNodeData = ShareLinkItemNode | ShareLinkRootNode | ShareLinkAssetNode;

export const isShareLinkItemNode = (node: BaseTreeNode): node is ShareLinkItemNode => {
  return node.type === TreeNodeType.ShareLinkItem;
};

export const isShareLinkRootNode = (node: BaseTreeNode): node is ShareLinkRootNode => {
  return node.type === TreeNodeType.ShareLinkRoot;
};

export const isShareLinkAssetNode = (node: BaseTreeNode): node is ShareLinkAssetNode => {
  return node.type === TreeNodeType.ShareLinkAsset;
};

//#end region

//#region release types

export interface ReleaseRootNode extends BaseTreeNode {
  type: TreeNodeType.ReleaseRoot;
}

export interface ReleaseItemNode extends BaseTreeNode {
  type: TreeNodeType.ReleaseItem;
  data: Release;
}

export interface ReleaseAssetNode extends BaseTreeNode {
  type: TreeNodeType.ReleaseAsset;
  data: ReleaseAssetItem;
  assetType: ReleaseAssetTypes;
}

export type ReleaseNode = ReleaseRootNode | ReleaseItemNode | ReleaseAssetNode;

export const isReleaseRootNode = (node: BaseTreeNode): node is ReleaseRootNode => {
  return node.type === TreeNodeType.ReleaseRoot;
};

export const isReleaseItemNode = (node: BaseTreeNode): node is ReleaseItemNode => {
  return node.type === TreeNodeType.ReleaseItem;
};

export const isReleaseAssetNode = (node: BaseTreeNode): node is ReleaseAssetNode => {
  return node.type === TreeNodeType.ReleaseAsset;
};

//#endregion

//#region project node types

export interface CustomNode extends BaseTreeNode {
  type: TreeNodeType.CustomNode;
  customType: string;
}

export type ProjectNode = ProjectRootNode | ProjectSectionNode | SubsectionRootNode | KpfFolderNode | CustomNode;

export const isProjectRootNode = (node: BaseTreeNode): node is ProjectRootNode => {
  return node.type === TreeNodeType.ProjectRoot;
};

export const isProjectSectionNode = (node: BaseTreeNode): node is ProjectSectionNode => {
  return node.type === TreeNodeType.ProjectSection;
};

//#endregion

//#region Custom node

export const isCustomNode = (node: BaseTreeNode): node is CustomNode => {
  return node.type === TreeNodeType.CustomNode;
};

export const isDashboardNode = (node: BaseTreeNode): node is CustomNode => {
  return isCustomNode(node) && node.customType === 'dashboard';
};

export interface TreeState<T extends BaseTreeNode> {
  root: T | null;
  paths: Record<string, string[]>;
  selectedNode: T | null;
  expandedNodes: T[];
  loading?: boolean;
}

//#endregion
export interface ToolbarNavigationState<T extends BaseTreeNode> {
  node: T | null;
  action: 'next' | 'previous' | 'direct' | 'default';
}
